import * as THREE from 'three/webgpu';
import type {SketchParametricOperationIntent} from '../../../types/sketch/solver/SketchParametricOperationIntent';
import {isSketchLineObject} from '../guards';

/**
 * 草图属性编辑服务输入。
 */
export interface SketchPropertyEditServiceInput {
    /** 被属性面板编辑的草图对象。 */
    target: THREE.Object3D;
    /** 属性编辑原因。 */
    reason: string;
    /** 触发本次编辑的对象 id。 */
    sourceObjectId?: string;
    /** 圆系属性面板本次明确输入的目标半径（米）。 */
    radiusTarget?: number;
    /** 直线属性面板本次明确输入的目标长度（米）。 */
    lineLengthTarget?: number;
    /** 是否在本次绘制态改长中保持直线起点。 */
    preserveLineStartPoint?: boolean;
}

/**
 * 草图属性编辑服务结果。
 */
export interface SketchPropertyEditServiceResult {
    /** 是否命中并提交了所属草图组。 */
    committed: boolean;
    /** 所属草图组 id；未命中时为 null。 */
    ownerSketchGroupId: string | null;
    /** 当前属性状态是否通过统一参数求解。 */
    solved: boolean;
}

/**
 * 所属草图组提交协议。
 */
interface SketchDefinitionCommitOwner {
    /** 草图组标记。 */
    isSketchGroup: true;
    /** 草图组 id。 */
    uuid: string;
    /**
     * 标记草图定义已变更。
     * @param input 变更上下文。
     */
    markDefinitionDirty(input: {
        reason: string;
        sourceObjectId?: string;
    }): void;
    /**
     * 提交草图定义变更。
     * @param input 提交上下文。
     */
    commitDefinitionChanges(input: {
        reason: string;
        sourceObjectId?: string;
    }): void;
    /**
     * 判断草图组是否存在未提交定义变更。
     * @returns 是否存在未提交变更。
     */
    hasUncommittedDefinitionChanges(): boolean;
}

/**
 * 判断对象是否为草图定义提交宿主。
 * @param value 待判断对象。
 * @returns 是否为草图定义提交宿主。
 */
function isSketchDefinitionCommitOwner(value: unknown): value is SketchDefinitionCommitOwner {
    return typeof value === 'object'
        && value !== null
        && Reflect.get(value, 'isSketchGroup') === true
        && typeof Reflect.get(value, 'markDefinitionDirty') === 'function'
        && typeof Reflect.get(value, 'commitDefinitionChanges') === 'function'
        && typeof Reflect.get(value, 'hasUncommittedDefinitionChanges') === 'function';
}

/**
 * 草图属性编辑服务。
 *
 * 设计说明：
 * 1. 属性面板编辑不是拖拽，因此不会自然进入“拖拽结束提交”链路；
 * 2. 这里把“统一参数求解 + 草图 dirty/commit”收口到底层 SDK，不调用对象级求解；
 * 3. web-client 只需要在属性编辑成功后调用本服务，避免每个面板重复拼装提交逻辑。
 */
export class SketchPropertyEditService {
    /**
     * @param solveParametricState 携带当前被编辑实体与声明式属性意图执行统一参数求解的抽象端口。
     * setter 已写入的候选状态由 Viewer 作为权威输入；直线长度等派生几何目标由操作意图释放必要自由度。
     */
    public constructor(
        private readonly solveParametricState: (
            target: THREE.Object3D,
            operationIntents: readonly SketchParametricOperationIntent[],
        ) => boolean,
    ) {}

    /**
     * 完成一次草图属性编辑。
     * @param input 属性编辑输入。
     * @returns 属性编辑收口结果。
     */
    public finalizeEdit(input: SketchPropertyEditServiceInput): SketchPropertyEditServiceResult {
        const ownerSketchGroup = this.findOwnerSketchGroup(input.target);
        if (!ownerSketchGroup) {
            return {
                committed: false,
                ownerSketchGroupId: null,
                solved: false,
            };
        }

        /** 属性声明或 setter 已形成候选；失败时由上层属性历史基线负责恢复 before。 */
        const operationIntents = this.buildOperationIntents(input);
        if (!this.solveParametricState(input.target, operationIntents)) {
            return {
                committed: false,
                ownerSketchGroupId: ownerSketchGroup.uuid,
                solved: false,
            };
        }

        /**
         * 有些定义式 setter 只更新对象内部几何，不会触发点位写入，
         * 因此这里在提交前补一次 dirty，确保 committed 事件一定能发出。
         */
        if (!ownerSketchGroup.hasUncommittedDefinitionChanges()) {
            ownerSketchGroup.markDefinitionDirty({
                reason: input.reason,
                sourceObjectId: input.sourceObjectId,
            });
        }

        ownerSketchGroup.commitDefinitionChanges({
            reason: input.reason,
            sourceObjectId: input.sourceObjectId,
        });

        return {
            committed: true,
            ownerSketchGroupId: ownerSketchGroup.uuid,
            solved: true,
        };
    }

    /**
     * 把属性面板的声明式输入转换为一次性参数求解意图。
     *
     * @param input 属性编辑输入。
     * @returns 不进入关系、尺寸或持久化的瞬态操作意图。
     */
    private buildOperationIntents(
        input: SketchPropertyEditServiceInput,
    ): readonly SketchParametricOperationIntent[] {
        const operationIntents: SketchParametricOperationIntent[] = [];
        if (input.radiusTarget !== undefined) {
            operationIntents.push({
                kind: 'entity-radius-target',
                intentId: `property-radius:${input.target.uuid}`,
                entityId: input.target.uuid,
                targetRadius: input.radiusTarget,
            });
        }
        if (input.lineLengthTarget !== undefined) {
            if (input.preserveLineStartPoint && isSketchLineObject(input.target)) {
                const startLocalPosition = input.target.startPoint.getLocalPosition2D();
                operationIntents.push({
                    kind: 'point-target',
                    intentId: `property-line-length-anchored:${input.target.uuid}`,
                    pointId: input.target.startPoint.uuid,
                    localPosition: [startLocalPosition.x, startLocalPosition.y],
                    temporaryConstraints: [{
                        kind: 'line-length',
                        constraintId: `property-line-length:${input.target.uuid}`,
                        entityId: input.target.uuid,
                        targetLength: input.lineLengthTarget,
                    }],
                });
                return operationIntents;
            }
            operationIntents.push({
                kind: 'entity-line-length-target',
                intentId: `property-line-length:${input.target.uuid}`,
                entityId: input.target.uuid,
                targetLength: input.lineLengthTarget,
            });
        }
        return operationIntents;
    }

    /**
     * 沿父链查找所属草图组。
     * @param target 起始对象。
     * @returns 所属草图组；未命中时返回 null。
     */
    private findOwnerSketchGroup(target: THREE.Object3D): SketchDefinitionCommitOwner | null {
        let current: THREE.Object3D | null = target;
        while (current) {
            if (isSketchDefinitionCommitOwner(current)) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }
}
